import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ClipLoader } from "react-spinners";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { RiSearchLine } from "react-icons/ri";
import { FiArrowRight } from "react-icons/fi";

import { API } from "../../constant/api";
import { APP_SINGLE_BUSINESS_URL } from "../../constant";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { comingSoon } from "../../assets/images";
import BusinessBrandHeader from "../../components/customer/BusinessBrandHeader";
import CustomerPageShell from "../../components/customer/CustomerPageShell";
import PageAvatar from "../../components/PageAvatar";

const CustomerPayment = () => {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<number>(1);
  const [query, setQuery] = useState("");

  const axiosInstance = useAuthAxios();
  // The public endpoint — this page is a link merchants hand to customers, who
  // are never logged in. It also carries the business identity, so the header
  // costs no extra round trip.
  const getPayments = async () => {
    const response = await axiosInstance.get(
      API.getPublicPaymentPages(id ?? "", page, query)
    );
    return response?.data;
  };

  const {
    data: payments,
    error: error1,
    isLoading: loading1,
    // Page + query belong in the key, otherwise search/pagination never refetch.
  } = useQuery({
    queryKey: ["get-payments", id, page, query],
    queryFn: getPayments,
    retry: 1,
  });

  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);

  const paymentsData = payments?.data?.pages;
  const totalPages = payments?.data?.meta?.totalPages || 0;
  const business = payments?.data?.business;

  // Search should only claim the "no results" copy when there genuinely was a
  // search — an empty business reads very differently from an empty result set.
  const isSearching = query.trim().length > 0;
  const businessName = business?.name;

  return (
    <>
      <Helmet>
        <title>{businessName ? `Pay ${businessName}` : "Payment options"}</title>
        <meta
          name="description"
          content={
            businessName
              ? `Choose how you'd like to pay ${businessName}.`
              : "Choose a payment option."
          }
        />
      </Helmet>

      <CustomerPageShell width="wide">
        {loading1 ? (
          <div className="flex min-h-[50vh] w-full items-center justify-center">
            <ClipLoader color="#0B84CB" size={56} />
          </div>
        ) : (
          <>
            <BusinessBrandHeader
              name={businessName}
              logo={business?.logo ?? undefined}
              tagline="Choose what you'd like to pay for below."
            />

            <div className="mt-9">
              {/* Search — hidden when there is nothing to search through. */}
              {(isSearching || paymentsData?.length >= 1) && (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition focus-within:border-secondary_button/50 focus-within:ring-4 focus-within:ring-secondary_button/10">
                  <RiSearchLine className="text-[19px] text-gray-400" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value.toLowerCase());
                      // A new search invalidates the current page number.
                      setPage(1);
                    }}
                    placeholder="Search payment options"
                    className="w-full bg-transparent text-[14.5px] text-gray-800 outline-none placeholder:text-gray-400"
                  />
                </div>
              )}

              {paymentsData?.length >= 1 ? (
                <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                  <div className="divide-y divide-gray-100">
                    {paymentsData?.map((data: any, index: number) => (
                      <button
                        key={index}
                        onClick={() => {
                          window.location.href = `${APP_SINGLE_BUSINESS_URL}/${data?.reference}`;
                        }}
                        className="group flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-50 mds:px-6 mds:py-5"
                      >
                        <div className="flex min-w-0 items-center gap-4">
                          <PageAvatar
                            name={data?.name}
                            image={data?.image}
                            className="size-11 shadow-sm"
                            textClassName="text-[16px]"
                          />
                          <div className="min-w-0">
                            <h4 className="truncate font-sora text-[15.5px] font-semibold text-gray-900">
                              {data?.name}
                            </h4>
                            {data?.description && (
                              <p className="mt-0.5 truncate text-[13px] font-medium text-gray-500">
                                {data.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex size-9 flex-none items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-all group-hover:border-secondary_button group-hover:bg-secondary_button group-hover:text-white">
                          <FiArrowRight className="text-[16px]" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200/70 bg-white px-6 py-14 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                  {isSearching ? (
                    <>
                      <div className="flex size-12 items-center justify-center rounded-full bg-gray-50">
                        <RiSearchLine className="text-[20px] text-gray-400" />
                      </div>
                      <h2 className="mt-4 font-sora text-[16px] font-bold text-gray-900">
                        Nothing matches “{query}”
                      </h2>
                      <p className="mt-1.5 max-w-[280px] text-[13.5px] font-medium text-gray-500">
                        Try a different word, or clear the search to see
                        everything.
                      </p>
                    </>
                  ) : (
                    <>
                      <img
                        src={comingSoon}
                        alt=""
                        className="h-auto w-[140px] max-w-full"
                      />
                      <h2 className="mt-5 font-sora text-[16px] font-bold text-gray-900">
                        No payment options yet
                      </h2>
                      <p className="mt-1.5 max-w-[280px] text-[13.5px] font-medium text-gray-500">
                        {businessName
                          ? `${businessName} hasn't published anything to pay for yet.`
                          : "There's nothing to pay for here yet."}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Pagination */}
              {paymentsData?.length >= 1 && totalPages > 1 && (
                <div className="mt-5 flex items-center justify-center gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => page > 1 && setPage((prev) => prev - 1)}
                    className="flex size-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <GoChevronLeft className="text-[18px]" />
                  </button>
                  <span className="px-1 text-[13px] font-semibold text-gray-500">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() =>
                      page < totalPages && setPage((prev) => prev + 1)
                    }
                    className="flex size-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <GoChevronRight className="text-[18px]" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </CustomerPageShell>
    </>
  );
};

export default CustomerPayment;
